import { Box, SelectChangeEvent, Stack } from '@mui/material'
import React, { FunctionComponent, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment, { Moment } from 'moment';
import { IBuildingType, ICountry, IPostLocation } from '../../../../shared/services/location/location.model'
import LocationService from '../../../../shared/services/location/location.service'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DialogSaveButtons } from '../../miscellaneous-components/dialog-save-buttons'
import { FormTextInputDisplay } from '../../form-field-components/new-and-edit/form-text-input'
import { FormRadioInputDisplay } from '../../form-field-components/new-and-edit/form-radio-input'
import { FormSelectInputDisplay } from '../../form-field-components/new-and-edit/form-select-input'
import { FormAutoCompleteInputDisplay } from '../../form-field-components/new-and-edit/form-autocomplete-input'
import { FormDateInputDisplay } from '../../form-field-components/new-and-edit/form-date-input'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model';

export const LocationNewForm: FunctionComponent<{
  submitHandler: (values: IPostLocation) => void,
  newLocation: IPostLocation,
  errorMessage: string,
  isSubmitting?: boolean | undefined,
  errorCode?: number,
}> = ({
  submitHandler,
  newLocation,
  errorMessage,
  isSubmitting,
  errorCode
}) => {
    const navigate = useNavigate()
    //Services
    const locationService = new LocationService();
    const customFieldService = new CustomFieldService();
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [customFields, setCustomFields] = useState<ICustomField[]>();
    const [buildingTypes, setBuildingTypes] = useState<IBuildingType[]>();
    const [countries, setCountries] = useState<ICountry[]>();
    // const [hasNoLocations, setHasNoLocations] = useState<boolean>(false)

    const ownership: string[] = [
      'Leased', 'Owned (Using)', 'Owned (Leased Out)'
    ]

    const locationSchema = Yup.object().shape({
      friendlyName: Yup.string().required(`${screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })} name is required`),
      buildingTypeId: Yup.string().required('Building type is required'),
      ownership: Yup.string().required('Ownership status is required'),
      hasOperationalControl: Yup.boolean().required('Control status is required'),
      countryId: Yup.string().required('Country is required'),
      startDate: Yup.string().required(`You must provide a start date for the ${screenLabelsContext.getLabel('location', { plural: false })}`),
      // isPrimaryLocation: Yup.boolean(),
      ...(customFields && customFields.length > 0 ? customFields.reduce((acc, field) => {
        if (field.isMandatory) {
          acc[field.id] = Yup.string().required(`${field.fieldName} is mandatory`);
        } else {
          acc[field.id] = Yup.string().nullable();
        }
        return acc;
      }, {} as any) : {}),
    })

    const formik = useFormik<any>({
      initialValues: {
        friendlyName: newLocation.friendlyName,
        buildingTypeId: newLocation.buildingTypeId,
        countryId: newLocation.countryId,
        startDate: newLocation.startDate,
        // isPrimaryLocation: newLocation.isPrimaryLocation,
        ownership: newLocation.ownership,
        hasOperationalControl: newLocation.hasOperationalControl,
      },
      validationSchema: locationSchema,
      onSubmit: (values) => {
        const customFieldValues = Array.isArray(customFields)
          ? customFields.map((field) => ({
            customFieldId: field.id,
            value: values?.[field.id] ?? null,
          }))
          : []
        submitHandler({
          ...values,
          customFieldValues,
        });
      }
    });

    useEffect(() => {
      locationService.getAllBuildingType()
        .then(response => {
          setBuildingTypes(response)
        })
        .catch(err => {
          console.log(err)
        })

      locationService.getAllCountries()
        .then(response => {
          setCountries(response)
        })
        .catch(err => {
          console.log(err)
        })

      // locationService.getActiveLocations()
      //   .then(response => {
          // if (response.length === 0) {
          //   setHasNoLocations(true)
          //   formik.setFieldValue('isPrimaryLocation', true)
          // }
        // })
        // .catch(err => console.log(err))

      customFieldService.getAllActive(`&entryType=location`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => {
          console.log(err)
        })
    }, [])

    const selectCountryHandler = (e: SelectChangeEvent<ICountry | null>, value: ICountry | null) => {
      if (value) {
        formik.setValues({ ...formik.values, countryId: value.id })
      }
    }

    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        formik.setValues({ ...formik.values, [name]: date.format('YYYY-MM-DD') })
      }
    }

    const checkboxHandler = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
      const { checked } = e.target;
      formik.setValues({ ...formik.values, [name]: checked })
    }

    const controlStatusHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === 'true';
      formik.setFieldValue('hasOperationalControl', value)
    }

    return (
      <Box component='form' onSubmit={formik.handleSubmit}>
        <FormTextInputDisplay
          isEditPage={false}
          allowEdit={false}
          disabled={false}
          title='Name'
          name='friendlyName'
          value={formik.values.friendlyName}
          touched={formik.touched.friendlyName}
          errors={formik.errors.friendlyName}
          onChange={formik.handleChange}
          customErrorMessage={errorCode === 1 ? errorMessage : undefined}
          isTopRow={true}
        />
        {/* <FormCheckBoxInputDisplay
          isEditPage={false}
          allowEdit={false}
          disabled={hasNoLocations ? true : false}
          title={`Primary ${screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })}`}
          name='isPrimaryLocation'
          value={formik.values.isPrimaryLocation}
          touched={formik.touched.isPrimaryLocation}
          errors={formik.errors.isPrimaryLocation}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => checkboxHandler(e, 'isPrimaryLocation')}
          label={`Primary ${screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })}`}
        /> */}
        <FormDateInputDisplay
          isEditPage={false}
          allowEdit={false}
          disabled={false}
          title='Start Date'
          name='startDate'
          value={moment(formik.values.startDate)}
          touched={formik.touched.startDate}
          errors={formik.errors.startDate}
          onChange={(date: any) => dateHandler(date, 'startDate')}
          customErrorMessage={errorCode === 5 ? errorMessage : undefined}
        />
        {countries &&
          <FormAutoCompleteInputDisplay
            isEditPage={false}
            allowEdit={false}
            disabled={false}
            title='Country'
            name='countryId'
            value={countries?.find(country => country.id === formik.values.countryId)}
            touched={formik.touched.countryId}
            errors={formik.errors.countryId}
            onChange={(e: any, value: ICountry | null) => selectCountryHandler(e, value)}
            options={countries.slice().sort((a, b) => (a.name === 'United Kingdom' ? -1 : b.name === 'United Kingdom' ? 1 : a.name.localeCompare(b.name)))}
            optionLabelKey={'name'}
            optionImage={'imageUrl'}
            customErrorMessage={errorCode === 2 ? errorMessage : undefined}
          />
        }
        {buildingTypes &&
          <FormSelectInputDisplay
            isEditPage={false}
            allowEdit={false}
            disabled={false}
            title='Building Type'
            name='buildingTypeId'
            value={formik.values.buildingTypeId}
            touched={formik.touched.buildingTypeId}
            errors={formik.errors.buildingTypeId}
            onChange={formik.handleChange}
            array={buildingTypes.sort((a, b) => a.name.localeCompare(b.name))}
            valueKey={'id'}
            labelKey={'name'}
            customErrorMessage={errorCode === 3 ? errorMessage : undefined}
          />}
        {ownership &&
          <FormSelectInputDisplay
            isEditPage={false}
            allowEdit={false}
            disabled={false}
            title='Ownership'
            name='ownership'
            value={formik.values.ownership}
            touched={formik.touched.ownership}
            errors={formik.errors.ownership}
            onChange={formik.handleChange}
            array={ownership}
            customErrorMessage={errorCode === 4 ? errorMessage : undefined}
          />}
        <FormRadioInputDisplay
          disabled={false}
          name='hasOperationalControl'
          isEditPage={false}
          allowEdit={false}
          title='Control Status'
          value={formik.values.hasOperationalControl}
          touched={formik.touched.hasOperationalControl}
          errors={formik.errors.hasOperationalControl}
          onChange={controlStatusHandler}
          optionOneLabel='Operationally Controlled'
          optionOneValue={true}
          optionTwoValue={false}
          optionTwoLabel='Not Operationally Controlled'
          isBottomRow={customFields && customFields.length > 0 ? false : true}
        />
        {customFields && customFields.length > 0 && customFields.map((field, index) => (
          <FormTextInputDisplay
            key={field.id}
            isBottomRow={index === customFields.length - 1}
            isEditPage={false}
            title={field.fieldName}
            name={field.id}
            disabled={false}
            type={field.fieldType === 'number' ? 'number' as const : 'text' as const}
            value={formik.values[field.id]}
            touched={formik.touched[field.id]}
            errors={formik.errors[field.id]}
            onChange={formik.handleChange}
          />
        ))
        }
        <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
          <DialogSaveButtons
            isForm={true}
            isSubmitting={isSubmitting}
            cancelHandler={() => navigate('/organisation/locations')}
            submitHandler={formik.submitForm}
          />
        </Stack>
      </Box >
    )
  }